import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Modal } from '../Modal/Modal';
import { ArrowRightIcon, ChevronDownIcon } from '../../assets/Icons';
import { Button } from '../Button';
import type { DonationFormState, DonationModalProps } from '../../types';

type Step = 1 | 2 | 3;

const presetAmounts = [100, 500, 1000];
const designations = [
  'Choose One',
  'Where most needed',
  'Education & Skill',
  'Healthcare',
  'Environment',
];

export const DonationModal = ({ isOpen, onClose }: DonationModalProps) => {
  const initialState: DonationFormState = useMemo(
    () => ({ amount: '', designation: '', dedicate: false, comment: false }),
    []
  );
  const [form, setForm] = useState<DonationFormState>(initialState);
  const [step, setStep] = useState<Step>(1);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setForm(initialState);
      setStep(1);
      setIsDropdownOpen(false);
    }
  }, [isOpen, initialState]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const closeAndReset = () => {
    onClose();
  };

  const nextDisabled = () => {
    if (step === 1) {
      return !(form.amount || 0) || form.amount === 0;
    }
    if (step === 2) {
      return !form.paymentMethod;
    }
    return false;
  };

  const renderStepHeader = () => {
    const titles: Record<Step, string> = {
      1: 'Donation Amount',
      2: 'Payment',
      3: 'Thank You',
    };
    return titles[step];
  };

  const handleNext = () => {
    if (step < 3) setStep(s => (s + 1) as Step);
  };
  const handleBack = () => {
    if (step > 1) setStep(s => (s - 1) as Step);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeAndReset}
      className='max-w-xl w-full overflow-hidden'
      showCloseButton={false}
    >
      {/* Modal header bar */}
      <div className='bg-seeds-dark text-white px-4 md:px-6 py-8 flex items-center justify-between rounded-t-2xl'>
        <button
          type='button'
          onClick={step === 1 ? closeAndReset : handleBack}
          className='flex items-center gap-2 text-white hover:opacity-90'
          aria-label='Back'
        >
          {step !== 3 && (
            <ArrowRightIcon className='w-5 h-5 md:w-8 md:h-8 rotate-180' />
          )}
          <span className='text-xl md:text-3xl font-satoshi-bold'>
            {renderStepHeader()}
          </span>
        </button>
        {step !== 3 && (
          <div className='flex items-center gap-2 md:gap-3'>
            {[1, 2, 3].map(i => (
              <span
                key={i}
                className={`w-2 h-2 md:w-5 md:h-5 rounded-full ${
                  step === i ? 'bg-white' : 'border md:border-2 border-white'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <div className='p-4 md:p-8'>
        {step === 1 && (
          <div className='space-y-6'>
            <div>
              <p className='text-lg md:text-xl text-[#4B4B4B] font-satoshi-medium mb-4'>
                Donate an Amount of your Choice *
              </p>
              <div className='grid grid-cols-3 gap-4'>
                {presetAmounts.map(amt => (
                  <button
                    key={amt}
                    type='button'
                    onClick={() => setForm(f => ({ ...f, amount: amt }))}
                    className={`border rounded-xl py-4 text-lg text-[#4B4B4B] font-inter-semibold ${form.amount === amt ? 'border-[#006543] bg-[#EAF5F1]' : 'border-gray-200'}`}
                  >
                    ₹ {amt}
                  </button>
                ))}
              </div>
              <div className='mt-4'>
                <div className='flex items-center border rounded-xl px-4'>
                  <span className='text-xl md:text-2xl mr-2'>₹</span>
                  <input
                    inputMode='numeric'
                    placeholder=''
                    value={form.amount === '' ? '' : String(form.amount)}
                    onChange={e => {
                      const v = e.target.value.replace(/[^0-9]/g, '');
                      setForm(f => ({ ...f, amount: v ? Number(v) : '' }));
                    }}
                    className='w-full py-3 outline-none text-2xl'
                  />
                </div>
              </div>
            </div>

            <div>
              <p className='text-lg md:text-xl text-[#3D498D] font-satoshi-medium mb-3'>
                Designation
              </p>
              <div className='relative' ref={dropdownRef}>
                <button
                  type='button'
                  className='w-full border rounded-xl px-4 py-3 text-sm md:text-lg bg-white text-left flex items-center justify-between cursor-pointer'
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span
                    className={
                      form.designation ? 'text-gray-900' : 'text-gray-500'
                    }
                  >
                    {form.designation || 'Choose One'}
                  </span>
                  <ChevronDownIcon
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      isDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isDropdownOpen && (
                  <div className='absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto'>
                    {designations.map((d, _index) => (
                      <button
                        key={d}
                        type='button'
                        className={`w-full px-4 py-3 text-sm md:text-lg text-left hover:bg-gray-50 first:rounded-t-xl last:rounded-b-xl ${
                          form.designation === (d === 'Choose One' ? '' : d)
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-900'
                        }`}
                        onClick={() => {
                          setForm(f => ({
                            ...f,
                            designation: d === 'Choose One' ? '' : d,
                          }));
                          setIsDropdownOpen(false);
                        }}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className='space-y-4'>
              <label className='flex items-center gap-3'>
                <input
                  type='checkbox'
                  checked={form.dedicate}
                  onChange={e =>
                    setForm(f => ({ ...f, dedicate: e.target.checked }))
                  }
                />
                <span className='text-gray-700 text-sm ms:text-lg'>
                  Dedicate my donation in honor or in memory of someone
                </span>
              </label>
              <label className='flex items-center gap-3'>
                <input
                  type='checkbox'
                  checked={form.comment}
                  onChange={e =>
                    setForm(f => ({ ...f, comment: e.target.checked }))
                  }
                />
                <span className='text-gray-700 text-sm ms:text-lg'>
                  Write us a comment
                </span>
              </label>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className='space-y-4'>
            <p className='text-xl md:text-2xl text-[#4B6357] font-inter-semibold'>
              Choose a payment option
            </p>
            {[
              'Apple Pay',
              'Google Pay',
              'PayPal',
              'Venmo',
              'Credit/Debit Card',
              'Bank Transfer',
            ].map(pm => (
              <button
                key={pm}
                type='button'
                onClick={() => setForm(f => ({ ...f, paymentMethod: pm }))}
                className={`w-full text-left border rounded-xl py-3 px-4 text-md md:text-lg ${form.paymentMethod === pm ? 'border-[#006543] bg-[#EAF5F1]' : 'border-gray-200'}`}
              >
                {pm}
              </button>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className='space-y-4'>
            <div className='text-[#4B6357] text:lg md:text-xl leading-8'>
              <p>
                Here you can thank your donors for being engaged in the
                Campaign.
              </p>
              <p className='mt-2'>
                Let them know how important their contribution was.
              </p>
            </div>
          </div>
        )}

        <div className='flex justify-center items-center mt-8'>
          {/* <Button
            variant='secondary'
            size='md'
            onClick={step === 1 ? closeAndReset : handleBack}
          >
            {step === 1 ? 'CLOSE' : 'BACK'}
          </Button> */}
          <Button
            variant='cta'
            size='md'
            disabled={nextDisabled()}
            onClick={step === 3 ? closeAndReset : handleNext}
            textColor='text-white'
            showArrow={step !== 3}
          >
            {step === 3 ? 'OK' : 'NEXT'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DonationModal;
